/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { Image } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { ROUTES } from '@sportninja/common/constants/app'
import { teamBudgetSlotRegistrationUrl } from '@sportninja/common/features/invitation/helper/teamBudgetSlot'
import type { TeamBudgetSlotRegistration } from '@sportninja/common/features/invitation/helper/teamBudgetSlot'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { resolveAssetUrl } from '@sportninja/common/utils/assetUrl'
import Button from 'src/components/ts-components/Button'
import {
  font,
  heading1,
  paragraphLarge,
  paragraphXSmall,
  weightBold,
  weightRegular,
} from 'src/components/css'

const TEAM_PLACEHOLDER = '/images/generic_placeholders/placeholder_team.png'

export interface TeamBudgetSlotInvite {
  type?: { id?: string | number | null } | null
  /** The team rep who is holding the slot. */
  sender?: string | null
  /** The team the slot is on. */
  destination?: {
    name?: string | null
    name_full?: string | null
    image?: unknown
  } | null
  registration?: TeamBudgetSlotRegistration | null
}

export interface TeamBudgetSlotInvitationProps {
  invite: TeamBudgetSlotInvite
  inviteeName?: string | null
  inviteeEmail?: string | null
}

/**
 * Jeff, Oct 1 — the invitation page for a Team Budget slot held for the invitee.
 *
 * The slot is taken by REGISTERING for the team, so the one action is "Register Now",
 * which opens the web wizard on that team, bound to this invitation. It never calls
 * accept: accepting adds nobody to the roster, and the server refuses it for this
 * type. "Not Now" leaves the slot held.
 *
 * The label does not repeat the team: the sentence above names it, and a button
 * label stays on one line, so a long team name pushed the button out of the card.
 *
 * Laid out like the family invitation beside it, so the two read as one family of
 * pages; the buttons are the shared ones.
 */
const TeamBudgetSlotInvitation = ({
  invite,
  inviteeName,
  inviteeEmail,
}: TeamBudgetSlotInvitationProps) => {
  const teamName =
    invite.destination?.name_full || invite.destination?.name || ''
  const competitionName = invite.registration?.competition_name || ''
  const registrationUrl = teamBudgetSlotRegistrationUrl(invite)
  const logo =
    resolveAssetUrl(getImageThumbnailId(invite.destination)) ?? TEAM_PLACEHOLDER

  return (
    <div css={pageStyle}>
      <div css={cardStyle} data-testid='team-budget-slot-invitation'>
        <Image
          width={80}
          height={80}
          src={logo}
          fallback={TEAM_PLACEHOLDER}
          preview={false}
          css={logoStyle}
        />

        <p css={titleStyle}>{capitalizeWords('A Spot Is Held For You')}</p>

        <div css={bodyStyle}>
          {inviteeName || inviteeEmail ? (
            <div css={inviteeRowStyle}>
              {inviteeName ? <p css={inviteeNameStyle}>{inviteeName}</p> : null}
              {inviteeEmail ? (
                <p css={inviteeEmailStyle}>({inviteeEmail})</p>
              ) : null}
            </div>
          ) : null}

          <p css={subtitleStyle}>
            {invite.sender ? (
              <>
                <span css={highlightStyle}>{invite.sender}</span> is holding a
                spot for you on{' '}
              </>
            ) : (
              <>A spot is held for you on </>
            )}
            <span css={highlightStyle}>{teamName}</span>
            {competitionName ? (
              <>
                {' '}
                in <span css={highlightStyle}>{competitionName}</span>
              </>
            ) : null}
            .
          </p>

          <p css={noteStyle}>
            {registrationUrl
              ? 'Register for the team to take it. Until you do, nobody else can.'
              : 'This spot can no longer be registered for. Ask the team for a new invitation.'}
          </p>
        </div>

        <div css={dividerStyle} />

        <div css={actionsStyle}>
          <Button
            variant='outlined'
            text={capitalizeWords(registrationUrl ? 'Not Now' : 'Go Home')}
            onClick={() => window.location.assign(ROUTES.HOME)}
          />
          {registrationUrl ? (
            <Button
              variant='primary'
              text={capitalizeWords('Register Now')}
              onClick={() => window.location.assign(registrationUrl)}
            />
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default TeamBudgetSlotInvitation

const pageStyle = css`
  width: 100%;
  height: 100%;
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  padding: 40px 24px;
`
const cardStyle = css`
  display: flex;
  width: 600px;
  padding: 40px 24px;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  border-radius: 16px;
  border: 1px solid ${colors.SECONDARY_300};
  background: ${colors.SECONDARY_400};
  @media (max-width: 768px) {
    max-width: 100vw;
    min-width: 0;
  }
`
const logoStyle = css`
  border-radius: 8px;
  object-fit: cover;
`
const titleStyle = css`
  margin: 0;
  color: ${colors.WHITE};
  text-align: center;
  text-transform: uppercase;
  ${heading1}
  ${font.heading}
`
const bodyStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`
const inviteeRowStyle = css`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 4px;
`
const inviteeNameStyle = css`
  margin: 0;
  color: ${colors.PRIMARY};
  text-align: center;
  ${paragraphLarge}
  ${weightBold}
`
const inviteeEmailStyle = css`
  margin: 0;
  ${paragraphXSmall}
  ${weightRegular}
  color: ${colors.WHITE};
`
const subtitleStyle = css`
  margin: 0;
  ${paragraphLarge}
  ${weightRegular}
  color: ${colors.WHITE};
  text-align: center;
`
const highlightStyle = css`
  ${weightBold}
  color: ${colors.PRIMARY};
`
const noteStyle = css`
  margin: 0;
  ${paragraphXSmall}
  ${weightRegular}
  color: ${colors.NEUTRAL_50};
  text-align: center;
`
const dividerStyle = css`
  background: ${colors.HEADER};
  height: 1px;
  width: 100%;
`
const actionsStyle = css`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
`
